import Skeleton from '@/global/components/Skeleton'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { minutesToHours as hr, hoursToMinutes as min } from '@acme/common'
import { bancoAtivo } from '@acme/core'
import { Divider } from '@mui/material'
import { Link } from 'global/components/Link'
import SaldoBox from 'global/components/SaldoBox'
import { hasPermission } from 'hooks/useBackendMenu'
import { useCalculo } from 'hooks/useCalculo'
import { useMemo } from 'react'

interface IProps {
    /** Renderizar apenas o frame */
    frameOnly?: boolean
}

const isZeroHora = (value?: string) => !value || min(value) === 0

export function CalculoFooter({ frameOnly }: IProps) {
    const { espelho } = useCalculo()

    const Funcionario = espelho?.Funcionario
    const RegraCalculo = Funcionario?.RegrasCalculo
    const SaldoHoras = (espelho as any)?.SaldoHoras?.SaldoEspelho
    const SaldoAnterior = (espelho as any)?.SaldoHoras?.SaldoAnterior
        ?.SaldoEspelho
    const periodoFechamentoPonto =
        (espelho as any)?.Empresa?.periodoFechamentoPonto ?? 0

    const controlarBancoHoras = useMemo(() => {
            if (!espelho?.competencia) return false
            if (periodoFechamentoPonto < 1) return false
            if (!RegraCalculo) return false

            return bancoAtivo(RegraCalculo, espelho.competencia)
        }, [periodoFechamentoPonto, RegraCalculo, espelho?.competencia]),
        podeCompensar = useMemo(() => {
            return !!RegraCalculo?.Porcentagens?.some(it => it.compensacao > -1)
        }, [Funcionario?.id, RegraCalculo?.Porcentagens])

    function Children() {
        if (frameOnly) return []
        if (!espelho) return <CalculoFooterLoading />

        return [
            <div key="horas-esperadas">
                <div className="flex justify-between gap-2 items-center">
                    <span className="text-nowrap text-sm font-semibold">
                        Horas a trabalhar:
                    </span>
                    <span
                        className={cn(
                            'text-nowrap',
                            isZeroHora(espelho?.totHrsATrabalhar) &&
                                'text-muted-foreground',
                        )}
                    >
                        {espelho?.totHrsATrabalhar ?? '000:00'}
                    </span>
                </div>
                <div className="flex justify-between gap-2 items-center">
                    <span className="text-nowrap text-sm font-semibold">
                        Horas trabalhadas:
                    </span>
                    <span
                        className={cn(
                            'text-nowrap',
                            isZeroHora(espelho?.totHrsTrabalhadas) &&
                                'text-muted-foreground',
                        )}
                    >
                        {espelho?.totHrsTrabalhadas ?? '000:00'}
                    </span>
                </div>
            </div>,
            <Divider orientation="vertical" key="divider-1" />,
            <div key="sobreaviso-adicional-noturno">
                <div className="flex justify-between gap-2 items-center">
                    <span className="text-nowrap text-sm font-semibold">
                        Sobreaviso:
                    </span>
                    <span
                        className={cn(
                            'text-nowrap',
                            isZeroHora(espelho?.totSobreaviso) &&
                                'text-muted-foreground',
                        )}
                    >
                        {espelho?.totSobreaviso ?? '00:00'}
                    </span>
                </div>
                <div className="flex justify-between gap-2 items-center">
                    <span className="text-nowrap text-sm font-semibold">
                        Adicional noturno:
                    </span>
                    <span
                        className={cn(
                            'text-nowrap',
                            isZeroHora(espelho?.totAdicNoturno) &&
                                'text-muted-foreground',
                        )}
                    >
                        {espelho?.totAdicNoturno ?? '00:00'}
                    </span>
                </div>
            </div>,

            !!espelho?.totDescontoDSR &&
                !['0', '00:00'].includes(String(espelho.totDescontoDSR)) && [
                    <Divider orientation="vertical" key="divider-2" />,
                    <div
                        key="desconto-dsr"
                        className="flex gap-x-2 items-center justify-evenly"
                    >
                        <div className="w-1/2 text-sm font-semibold">
                            Descontos de DSR:{' '}
                        </div>
                        <div className="w-1/2">{espelho?.totDescontoDSR}</div>
                    </div>,
                ],

            <div className="flex flex-1" key="flex" />,

            Funcionario?.pontoDiferenciado === 'NORMAL' &&
                controlarBancoHoras && (
                    <SaldoBox
                        key="saldo-anterior"
                        saldo
                        title={
                            <Tooltip
                                title={
                                    <span>
                                        Saldo do banco de horas
                                        <br />
                                        acumulado até a
                                        <br />
                                        competência passada
                                    </span>
                                }
                            >
                                <div className="flex flex-col pl-1 cursor-help">
                                    <span>Saldo</span>
                                    <span>anterior</span>
                                </div>
                            </Tooltip>
                        }
                        extras={SaldoAnterior?.horasExtras}
                        faltas={SaldoAnterior?.faltas}
                        porcentagens={SaldoAnterior?.porcentagemHorasExtras}
                        porcentagensNoturnas={
                            SaldoAnterior?.porcentagemHorasExtrasNoturnas
                        }
                        tipoFaltas={{
                            Justificadas:
                                SaldoAnterior?.totalFaltasJustificadas,
                            Injustificadas:
                                SaldoAnterior?.totalFaltasInjustificadas,
                            Parciais: SaldoAnterior?.totFaltasParciais,
                            Integrais: SaldoAnterior?.totFaltasIntegrais,
                        }}
                    />
                ),
            <SaldoBox
                key="horas-mes"
                underline={!podeCompensar}
                title={
                    <Tooltip
                        title={
                            <span>
                                Todas as horas efetuadas no mês,
                                <br />
                                sem considerar compensações
                            </span>
                        }
                    >
                        <div className="flex flex-col pl-1 cursor-help">
                            <span>Horas</span>
                            <span>do mês</span>
                        </div>
                    </Tooltip>
                }
                extras={hr(
                    min(espelho?.totHrsExtras) +
                        min(espelho?.totHrsExtrasNoturnas),
                )}
                faltas={espelho?.totFaltas}
                porcentagens={espelho?.porcentagemHrsExtras as any}
                porcentagensNoturnas={
                    espelho?.porcentagemHrsExtrasNoturnas as any
                }
                tipoFaltas={{
                    Justificadas: espelho?.totalFaltasJustificadas,
                    Injustificadas: espelho?.totalFaltasInjustificadas,
                    Parciais: espelho?.totFaltasParciais,
                    Integrais: espelho?.totFaltasIntegrais,
                }}
            />,
            !!espelho?.PagamentoHoras &&
                (!!min(espelho.PagamentoHoras.horas) ||
                    !!min(espelho.PagamentoHoras.faltas)) && (
                    <SaldoBox
                        key="pagamento-horas"
                        title={hasPermission(
                            ['read', 'PagamentoDeHoras'],
                            () => (
                                <Link
                                    href={`/manutencao-ponto/pagamento-horas/editar/${Funcionario?.id}`}
                                    underline="hover"
                                >
                                    <div className="flex flex-col pl-1">
                                        <span>Pagamento</span>
                                        <span>da folha</span>
                                    </div>
                                </Link>
                            ),
                            () => (
                                <div className="flex flex-col pl-1">
                                    <span>Pagamento</span>
                                    <span>da folha</span>
                                </div>
                            ),
                        )}
                        extras={espelho?.PagamentoHoras?.horas}
                        faltas={hr(min(espelho?.PagamentoHoras?.faltas))}
                        porcentagens={
                            espelho?.PagamentoHoras?.porcentagens as any
                        }
                    />
                ),
            Funcionario?.pontoDiferenciado === 'NORMAL' && podeCompensar && (
                <SaldoBox
                    key="saldo-atual"
                    underline
                    saldo
                    title={
                        <Tooltip
                            title={
                                <span>
                                    Saldo atual do banco de horas, considerando
                                    <br />
                                    todas as compensações feitas no mês e
                                    <br />
                                    somando o saldo anterior (se houver)
                                </span>
                            }
                        >
                            <div className="flex flex-col pl-1 cursor-help">
                                <span>Saldo</span>
                                <span>atual</span>
                            </div>
                        </Tooltip>
                    }
                    extras={SaldoHoras?.horasExtras}
                    faltas={SaldoHoras?.faltas}
                    tipoFaltas={{
                        Parciais: SaldoHoras?.faltasParciais,
                        Integrais: SaldoHoras?.faltasIntegrais,
                        Justificadas: SaldoHoras?.faltasJustificadas,
                        Injustificadas: SaldoHoras?.faltasInjustificadas,
                    }}
                    porcentagens={SaldoHoras?.porcentagemHorasExtras}
                    porcentagensNoturnas={
                        SaldoHoras?.porcentagemHorasExtrasNoturnas
                    }
                />
            ),
        ]
    }

    return <CalculoFooterFrame>{Children()}</CalculoFooterFrame>
}

function CalculoFooterLoading() {
    return [
        <div key="horas-esperadas" className="flex flex-row items-center gap-4">
            {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-center flex-col gap-2">
                    <Skeleton className="h-4 w-[165px]" />
                    <Skeleton className="h-4 w-[165px]" />
                </div>
            ))}
        </div>,
        <div key="divider" className="flex flex-1" />,
        <div
            key="sobreaviso-adicional-noturno"
            className="flex items-center flex-row gap-2"
        >
            {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center flex-row gap-2">
                    <Skeleton className="h-7 w-[70px]" />
                    <div className="gap-1 flex flex-col">
                        <Skeleton className="h-4 w-[110px]" />
                        <Skeleton className="h-4 w-[110px]" />
                    </div>
                </div>
            ))}
        </div>,
    ]
}

export function Frame({ children }: { children?: React.ReactNode }) {
    return (
        <div
            className={cn(
                'min-h-15 max-h-15 w-full overflow-x-auto overflow-y-hidden rounded-b-sm scrollbar-thin',
                'border bg-card',
            )}
        >
            <div className="flex gap-2 items-center size-full p-2 overflow-y-hidden min-h-full">
                {children}
            </div>
        </div>
    )
}

export const CalculoFooterFrame = Object.assign(Frame, {
    LoadingContent: CalculoFooterLoading,
})
